// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/components/CompetitorStoreList/CompetitorStoreList.tsx
/* eslint-disable react/jsx-key */
import { format, parseISO } from 'date-fns';
import CurrencyFormat from 'react-currency-format';
import { CompetitorPriceDTO } from '../../../../../../../@types/IPA/GerenciadorDePrecos';
import { Competitors } from '../../../../../../../components';
import { CompetitorPriceDTOProps, NegotiationDataItemProps } from '../../NegociacaoFornecedor.types';

const originPrice = {
  'INFOPRICE - INFOPANEL': 'INFOPANEL',
  'INFOPRICE - MPDV': 'MPDV',
} as const;

export type ICompetitorStoreList = {
  competitorPrices: CompetitorPriceDTOProps[];
  retailPrice: NegotiationDataItemProps['retailPrice'];
  onClickPhoto: (photo?: CompetitorPriceDTO['photo']) => void;
};

// O par de traços que o cartão já mostra no lugar da data ausente, agora também no lugar do preço e da
// competitividade ausentes.
const MISSING_VALUE_MARK = '--';

/**
 * A competitividade, ou ausência. Preço de concorrente nulo NÃO é zero: dividir por ele dá `Infinity`,
 * que saía desenhado na tela como "Infinity%" — foi o defeito de 21/08/2026, no cartão gêmeo deste.
 */
const competitivenessPercentage = (retailPrice: ICompetitorStoreList['retailPrice'], competitorRetailPrice?: number | null) => {
  if (retailPrice === null || retailPrice === undefined) return null;
  if (competitorRetailPrice === null || competitorRetailPrice === undefined) return null;
  if (competitorRetailPrice === 0) return null;

  return (retailPrice / competitorRetailPrice) * 100;
};

export const CompetitorStoreList = ({ competitorPrices, retailPrice, onClickPhoto }: ICompetitorStoreList) => (
  <Competitors.Section>
    <Competitors.Header>
      <Competitors.Icon icon="DescArrowForward" />
      <Competitors.Title>Lojas</Competitors.Title>
    </Competitors.Header>

    <Competitors.Content>
      {competitorPrices.map(competitor => {
        const competitiveness = competitivenessPercentage(retailPrice, competitor.retailPrice);

        return (
          <Competitors.Card>
            <Competitors.CardHeader>
              <Competitors.Title>{competitor.competitorStore}</Competitors.Title>
              <Competitors.CardHeaderAction>
                <Competitors.Tag>{originPrice[competitor.origemPreco]}</Competitors.Tag>
                <Competitors.Label>{competitor.competitorStoreType}</Competitors.Label>
                {competitor.photo && (
                  <Competitors.PhotoIcon
                    onClick={() => {
                      onClickPhoto(competitor.photo);
                    }}
                  />
                )}
              </Competitors.CardHeaderAction>
            </Competitors.CardHeader>

            <Competitors.CardContent>
              <Competitors.InfoBox>
                <Competitors.Label>Preço</Competitors.Label>
                <Competitors.Value>
                  {/* O formatador de moeda trata `undefined`, e não `null`: com null ele cai num
                    replace sobre nulo e derruba o cartão inteiro. Preço de concorrente ausente é
                    comum, porque a origem daquele dado não tem cobertura de tudo. */}
                  {competitor?.retailPrice == null ? (
                    MISSING_VALUE_MARK
                  ) : (
                    <CurrencyFormat
                      fixedDecimalScale
                      decimalScale={2}
                      value={competitor.retailPrice}
                      displayType="text"
                      prefix="R$ "
                      decimalSeparator=","
                      thousandSeparator="."
                    />
                  )}
                </Competitors.Value>
              </Competitors.InfoBox>

              <Competitors.InfoBox>
                <Competitors.Label>Competitividade</Competitors.Label>
                <Competitors.Value>
                  {competitiveness === null ? (
                    MISSING_VALUE_MARK
                  ) : (
                    <CurrencyFormat
                      fixedDecimalScale
                      decimalScale={2}
                      value={competitiveness}
                      displayType="text"
                      suffix="%"
                      decimalSeparator=","
                      thousandSeparator="."
                    />
                  )}
                </Competitors.Value>
              </Competitors.InfoBox>
              <Competitors.InfoBox>
                <Competitors.Label>Data</Competitors.Label>
                <Competitors.Value>{competitor.date ? format(parseISO(competitor.date), 'dd/MM/yyyy') : MISSING_VALUE_MARK}</Competitors.Value>
              </Competitors.InfoBox>
            </Competitors.CardContent>
          </Competitors.Card>
        );
      })}
    </Competitors.Content>
  </Competitors.Section>
);
